import { Placement } from "react-joyride";

export type IntroTourApp = "client" | "manager";

/**
 * Um passo do tour de introdução.
 * O alvo é o elemento marcado com `data-tour={id}` (use `tourAnchor(id)`).
 * Passos cujo alvo não existe ou está oculto (sem permissão, funcionalidade desligada) são pulados.
 */
export type IntroTourStep = {
    id: string;
    title: string;
    content: string;
    placement?: Placement | "center";
    /** O passo aponta para um item do menu lateral, que precisa estar aberto. */
    inMenu?: boolean;
};

/** Item de menu lateral que participa do tour quando tem o campo `tour`. */
export type IntroTourMenuItem = {
    id: number;
    text: string;
    menuId: number;
    tour?: string;
};

export const WELCOME_STEP_ID = "welcome";
export const FINISH_STEP_ID = "finish";
/** Passos de abertura e encerramento: só aparecem no tour completo. */
export const FRAMING_STEP_IDS = [WELCOME_STEP_ID, FINISH_STEP_ID];

export const tourAnchor = (id: string) => ({ "data-tour": id });
export const menuTourId = (menuItemId: number) => `menu-${menuItemId}`;

type RegistryStep = IntroTourStep & { apps: IntroTourApp[] };

const WELCOME_STEP: IntroTourStep = {
    id: WELCOME_STEP_ID,
    title: "Bem-vindo ao GTF Mapas",
    content: "Este tour rápido mostra o menu lateral, os botões do mapa e a grade de veículos. Você pode pular a qualquer momento.",
    placement: "center",
};

const FINISH_STEP: IntroTourStep = {
    id: FINISH_STEP_ID,
    title: "Tudo pronto",
    content: "Para ver este tour de novo, use o botão de ajuda na barra do mapa.",
    placement: "center",
};

const MENU_TOGGLE_STEP: RegistryStep = {
    id: "menu-toggle",
    title: "Menu lateral",
    content: "Abre e fecha o menu com todas as funções do sistema.",
    placement: "right",
    apps: ["client", "manager"],
};

// Passos do mapa, na ordem em que aparecem. Para uma função nova no mapa:
// marque o botão com {...tourAnchor("map-xxx")} e adicione o passo aqui.
const MAP_STEPS: RegistryStep[] = [
    {
        id: "map-alerts",
        title: "Alertas e eventos",
        content: "Mostra os alertas de veículos, condutores e desvios de rota. O número indica quantos estão pendentes.",
        placement: "left",
        apps: ["client", "manager"],
    },
    {
        id: "map-new-tab",
        title: "Nova aba",
        content: "Abre o rastreamento em outra aba, para acompanhar mais de um cliente ao mesmo tempo.",
        placement: "left",
        apps: ["manager"],
    },
    {
        id: "map-select-customer",
        title: "Selecionar cliente",
        content: "Troca o cliente exibido no mapa.",
        placement: "left",
        apps: ["manager"],
    },
    {
        id: "map-geofences",
        title: "Cercas virtuais",
        content: "Mostra ou oculta as cercas virtuais no mapa.",
        placement: "left",
        apps: ["client"],
    },
    {
        id: "map-interest-points",
        title: "Pontos de interesse",
        content: "Mostra ou oculta os pontos de interesse cadastrados.",
        placement: "left",
        apps: ["client"],
    },
    {
        id: "map-filter",
        title: "Filtrar veículos",
        content: "Escolhe quais veículos aparecem no mapa.",
        placement: "left",
        apps: ["client", "manager"],
    },
    {
        id: "map-layers",
        title: "Tipo de mapa",
        content: "Alterna entre OpenStreetMap e Google Maps.",
        placement: "left",
        apps: ["client", "manager"],
    },
    {
        id: "map-search",
        title: "Pesquisar endereço",
        content: "Busca um endereço no mapa e encontra o veículo mais próximo dele.",
        placement: "left",
        apps: ["client", "manager"],
    },
    {
        id: "map-grid",
        title: "Grade de veículos",
        content: "Abre a lista de veículos com status, última posição e endereço. Na grade você pesquisa, exporta e abre os detalhes de cada veículo.",
        placement: "left",
        apps: ["client", "manager"],
    },
    {
        id: "map-tour-replay",
        title: "Rever o tour",
        content: "Clique aqui quando quiser ver esta introdução de novo.",
        placement: "left",
        apps: ["client", "manager"],
    },
];

const forApp = (app: IntroTourApp, steps: RegistryStep[]): IntroTourStep[] =>
    steps.filter((step) => step.apps.includes(app)).map(({ apps, ...step }) => step);

/**
 * Monta os passos do tour do rastreamento.
 * Os passos do menu saem dos próprios itens do menu: basta o item ter o campo `tour`.
 */
export const buildIntroTourSteps = (app: IntroTourApp, menuItems: IntroTourMenuItem[]): IntroTourStep[] => {
    const menuSteps: IntroTourStep[] = menuItems
        .filter((item) => !!item.tour)
        // O menu é renderizado por grupo (menuId), na ordem dos itens dentro do grupo.
        .map((item, index) => ({ item, index }))
        .sort((a, b) => a.item.menuId - b.item.menuId || a.index - b.index)
        .map(({ item }) => ({
            id: menuTourId(item.id),
            title: item.text,
            content: item.tour as string,
            placement: "right",
            inMenu: true,
        }));

    return [WELCOME_STEP, ...forApp(app, [MENU_TOGGLE_STEP]), ...menuSteps, ...forApp(app, MAP_STEPS), FINISH_STEP];
};
